import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, RotateCw, Sparkles, AlertCircle, Check, Target, X, Plus } from "lucide-react";
import { formatPrice } from "@/lib/format";
import { fetchJson } from "@/lib/api";
import { EVENTS, track } from "@/lib/analytics";
import { aiErrorMessage } from "@/lib/serverMessages";
import { useAuth } from "@/auth/AuthProvider";
import { useOrder } from "@/store/orderStore";
import { useTenant } from "@/tenant/TenantProvider";
import { parseAllergyTokens } from "@/data/allergens";

// Maps quiz answer IDs to the labels the backend expects. These are protocol,
// not copy: nutrition.py matches goals and restrictions on these exact strings,
// so they stay English whatever the guest is reading.
const HUNGER_LABEL = { light: "Light", medium: "Medium", high: "Very Hungry" };
const PROTEIN_LABEL = { beef: "Beef", chicken: "Chicken", fish: "Salmon", eggs: "Eggs / Veg" };
const GOAL_LABEL = {
  protein: "Max Protein",
  energy: "Energy",
  fatloss: "Fat Loss",
  balanced: "Balanced",
};
const RESTRICTION_LABEL = {
  dairy: "Dairy Free",
  gluten: "Gluten Free",
  noraw: "No Raw",
  none: "None",
};

// The same goals, in the guest's own language — the quiz already names them, so
// the badge on a plate reads exactly as the question that produced it.
const GOAL_KEY = {
  protein: "quiz.goalProtein",
  energy: "quiz.goalEnergy",
  fatloss: "quiz.goalFatloss",
  balanced: "quiz.goalBalanced",
};

const formatTimeOfDay = () => {
  const d = new Date();
  const hh = d.getHours();
  const mm = String(d.getMinutes()).padStart(2, "0");
  let band = "Midday";
  if (hh < 11) band = "Morning";
  else if (hh < 16) band = "Midday";
  else if (hh < 19) band = "Afternoon";
  else band = "Evening";
  return `${band} ${String(hh).padStart(2, "0")}:${mm}`;
};

const AiPlateResult = ({ answers, freeText, onBack, onBuild, onAccept }) => {
  const { user } = useAuth();
  const { state } = useOrder();
  const guest = state.guest;
  const { slug, restaurant, findDish } = useTenant();
  const { t, i18n } = useTranslation();
  // The rotating "thinking" lines. `returnObjects` gives the array from the
  // catalog; a catalog that ever loses it must not take the screen down with it.
  const loadingMessages = t("ai.loading", { returnObjects: true });
  const messages = Array.isArray(loadingMessages) ? loadingMessages : [String(loadingMessages)];
  const [plates, setPlates] = useState(null); // array
  // Identifies this AI suggestion; travels with the order so the owner dashboard
  // can tell AI-driven revenue from manual ordering.
  const [requestId, setRequestId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [msgIdx, setMsgIdx] = useState(0);
  const [retryFlag, setRetryFlag] = useState(0);
  const [variation, setVariation] = useState(false);
  // One plate per answer: the first tap wins and the buttons die with it. Without
  // this, a double-tap (or a second card in the race window before navigation)
  // put the plate in the cart twice.
  const acceptedRef = useRef(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (!loading) return;
    const timer = setInterval(() => setMsgIdx((i) => (i + 1) % messages.length), 1100);
    return () => clearInterval(timer);
  }, [loading, messages.length]);

  useEffect(() => {
    let cancelled = false;
    const fetchPlates = async () => {
      setLoading(true);
      setError(null);
      // What the guest actually waits through. The server's own latency is in
      // `llm_usage`; this is the number with the network and the queue in it,
      // which is the one that decides whether they are still here at the end.
      const startedAt = Date.now();
      try {
        const data = await fetchJson("/auto-plate", {
          method: "POST",
          body: JSON.stringify({
            restaurant_slug: slug,
            hunger: HUNGER_LABEL[answers.hunger],
            protein: PROTEIN_LABEL[answers.protein],
            goal: GOAL_LABEL[answers.goal],
            restrictions: RESTRICTION_LABEL[answers.restriction],
            free_text: freeText || "",
            time_of_day: formatTimeOfDay(),
            variation,
            // The plate's own prose — its name and the sentence explaining it —
            // is the most persuasive text on this screen, and it used to arrive
            // in English under a translated label. Dish names stay as the menu
            // writes them: they travel to the kitchen ticket.
            language: i18n.language,
            // What they picked on the welcome screen, whoever they are. It used
            // to travel with the order and stop at the kitchen ticket, so a
            // guest who declared milk on the first screen could be handed a
            // plate built around it — and most guests never sign in, so the
            // account profile was no answer.
            guest_allergies: parseAllergyTokens(guest.allergy),
            user_profile: user
              ? {
                  goal: user.goal,
                  gender: user.gender,
                  age: user.age,
                  weight_kg: user.weight_kg,
                  height_cm: user.height_cm,
                  favorite_dishes: user.favorite_dishes,
                  allergies: user.allergies,
                  exclude_ingredients: user.exclude_ingredients,
                }
              : null,
          }),
        });
        const list = Array.isArray(data?.plates) ? data.plates : [];
        if (list.length === 0) {
          // Ours, not the server's: the call succeeded and came back empty.
          const empty = new Error("no plates");
          empty.code = "ai_empty";
          throw empty;
        }
        if (!cancelled) {
          setPlates(list);
          setRequestId(data.request_id || null);
          setLoading(false);
          track(EVENTS.AI_PLATES_SHOWN, {
            plates: list.length,
            goal: answers.goal,
            restriction: answers.restriction,
            hunger: answers.hunger,
            protein: answers.protein,
            variation,
            latency_ms: Date.now() - startedAt,
            // Whether every plate met its goal's numbers, or one was only the
            // closest the menu could get. The guest is told; so is the report.
            plates_meeting_goal: list.filter((p) => p.fit?.meets_goal).length,
            with_drink: list.filter((p) => (p.items || []).some((it) => findDish(it.id)?.category === "drinks")).length,
          });
        }
      } catch (e) {
        if (!cancelled) {
          // The error object is kept, not a sentence: the screen renders it in
          // the guest's language at paint time.
          setError(e);
          setLoading(false);
          track(EVENTS.AI_PLATES_FAILED, {
            reason: e.code || (e.status ? `http_${e.status}` : "network"),
            latency_ms: Date.now() - startedAt,
            variation,
          });
        }
      }
    };
    fetchPlates();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryFlag]);

  const handleTryAnother = () => {
    // Asking for other options is the same guest on the same meal — which is why
    // the conversion the dashboard reports counts sittings, not calls. Here it is
    // its own event, because it is also what the second model call costs.
    track(EVENTS.AI_TRY_ANOTHER, { goal: answers.goal });
    setVariation(true);
    setRetryFlag((x) => x + 1);
  };
  const handleRetry = () => {
    setRetryFlag((x) => x + 1);
  };

  if (loading) {
    return (
      <div className="px-5 pt-6 pb-10 min-h-screen flex flex-col">
        <Header onBack={onBack} subtitle={t("ai.buildingSubtitle")} />
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="relative w-20 h-20 mb-6">
            <div className="absolute inset-0 rounded-full bg-[#E34A27]/10 animate-ping" />
            <div className="absolute inset-2 rounded-full bg-[#E34A27] flex items-center justify-center">
              <Sparkles size={28} className="text-white" />
            </div>
          </div>
          <h2 data-testid="ai-loading" className="text-2xl font-black tracking-tight text-neutral-900 max-w-[24ch]">
            {messages[msgIdx % messages.length]}
          </h2>
          <p className="text-sm text-neutral-500 mt-3">
            {/* The restaurant names its own plate builder; falling back to the
                product's name rather than to one tenant's persona. */}
            {t("ai.thinking", { persona: restaurant?.ai?.persona_name || "Fuelly" })}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-5 pt-6 pb-10 min-h-screen flex flex-col">
        <Header onBack={onBack} subtitle={t("ai.overline")} />
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <AlertCircle className="text-red-500" />
          </div>
          <h2 className="text-xl font-black text-neutral-900">{t("ai.errorTitle")}</h2>
          <p data-testid="ai-error" className="text-sm text-neutral-500 mt-2 max-w-[36ch]">
            {aiErrorMessage(t, error)}
          </p>
          <button
            data-testid="ai-retry"
            onClick={handleRetry}
            className="mt-6 px-6 h-12 rounded-2xl bg-neutral-900 text-white font-bold text-sm"
          >
            {t("common.tryAgain")}
          </button>
          <button
            data-testid="ai-build-manually"
            onClick={onBuild}
            className="mt-3 text-sm text-neutral-500 underline underline-offset-4"
          >
            {t("ai.buildManually")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-5 pt-6 pb-10 min-h-screen flex flex-col">
      <Header onBack={onBack} subtitle={t("ai.builtForYou")} />
      <h1 className="text-[28px] leading-[1.1] font-black tracking-tight text-neutral-900 mb-1 mt-1">
        {t("ai.title")}
      </h1>
      <p className="text-sm text-neutral-500 mb-5">
        {t("ai.subtitle", { count: plates.length })}
      </p>

      <div className="space-y-5">
        {plates.map((plate, idx) => (
          <PlateCard
            key={idx}
            plate={plate}
            isTopPick={idx === 0}
            goalLabel={t(GOAL_KEY[answers.goal] || GOAL_KEY.balanced)}
            testIdPrefix={`plate-${idx}`}
            disabled={accepted}
            onAccept={(taken) => {
              if (acceptedRef.current) return;
              acceptedRef.current = true;
              setAccepted(true);
              track(EVENTS.AI_PLATE_ACCEPTED, {
                plate_index: idx,
                goal: answers.goal,
                items: (taken.items || []).length,
                // What the plate lost on the way into the cart: the drink is the
                // one line a guest can decline, and how often they do is the
                // whole argument for it being declinable.
                dropped_items: (plate.items || []).length - (taken.items || []).length,
                // The plate as proposed, before any edit — the totals travel with
                // the suggestion, not with what was kept. What was actually paid
                // is the server's own `order_placed`, and only that.
                suggested_price: plate.totals?.grand,
                variation,
              });
              // Which of the three was taken. Fire and forget: this is our
              // bookkeeping, and a guest heading for the cart must never wait on
              // it, nor see it fail.
              if (requestId) {
                fetchJson(`/auto-plate/${requestId}/accept`, {
                  method: "POST",
                  body: JSON.stringify({ plate_index: idx }),
                }).catch(() => {});
              }
              onAccept(taken, requestId);
            }}
          />
        ))}
      </div>

      <button
        data-testid="ai-try-another"
        onClick={handleTryAnother}
        className="mt-6 w-full min-h-12 py-2 px-4 rounded-2xl bg-white border border-neutral-200 text-neutral-700 font-bold text-sm active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
      >
        <RotateCw size={14} className="shrink-0" /> {t("ai.tryOther")}
      </button>
    </div>
  );
};

const Header = ({ onBack, subtitle }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 mb-3">
      <button
        data-testid="ai-back"
        onClick={onBack}
        className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center shrink-0"
        aria-label={t("common.back")}
      >
        <ArrowLeft size={18} />
      </button>
      <div className="min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500">
          {t("ai.overline")}
        </div>
        <div className="text-base font-bold text-neutral-900 -mt-0.5">{subtitle}</div>
      </div>
    </div>
  );
};

/**
 * The plate's numbers after the guest left something out.
 *
 * Display only, and never the price that is charged: the server re-prices every
 * order from the menu whatever this shows. It exists so the total on the card
 * matches the plate the guest is actually looking at.
 */
const recomputeTotals = (items, taxRate, serviceRate) => {
  const sum = (field) => items.reduce((acc, it) => acc + (Number(it[field]) || 0), 0);
  const subtotal = sum("price");
  const tax = subtotal * taxRate;
  const service = subtotal * serviceRate;
  return {
    p: sum("p"), f: sum("f"), c: sum("c"), kcal: sum("kcal"),
    subtotal, tax, service, grand: subtotal + tax + service,
  };
};

/**
 * One suggested plate.
 *
 * The plate's name, its `why` and the item notes are the model's own words and
 * arrive in English — they describe food, and repair may have edited them, so
 * they are shown exactly as the server sent them.
 */
const PlateCard = ({ plate, isTopPick, goalLabel, testIdPrefix, onAccept, disabled }) => {
  const { t: translate } = useTranslation();
  const { findDish, taxRate, serviceRate } = useTenant();
  // The model now includes a drink that fits the goal, because a meal is what
  // somebody actually orders. That is only fair if leaving it out is one tap —
  // an extra line a guest cannot decline is not a suggestion, it is a bundle.
  const [dropped, setDropped] = useState(() => new Set());
  const isDrink = (item) => findDish(item.id)?.category === "drinks";

  const kept = plate.items.filter((it) => !dropped.has(it.id));
  const edited = dropped.size > 0;
  const t = edited ? recomputeTotals(kept, taxRate, serviceRate) : plate.totals;
  // The server scores every plate against the goal's numbers and repairs a miss
  // with real menu moves. The guest was never told, so the one guarantee that
  // separates this from a guess was invisible on the screen that needed it.
  // Once the guest edits the plate the check no longer describes what is on it.
  const fit = edited ? null : plate.fit;

  const toggle = (id) =>
    setDropped((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else {
        next.add(id);
        // Only the drop, not the undo: what this answers is how often the drink
        // the model added was one the guest did not want. Putting it back is
        // them changing their mind, which is a different question.
        track(EVENTS.AI_PLATE_ITEM_REMOVED, { kind: "drink" });
      }
      return next;
    });

  return (
    <div
      data-testid={testIdPrefix}
      className={`relative rounded-3xl p-5 border-2 bg-white ${
        isTopPick ? "border-neutral-900" : "border-neutral-200"
      }`}
    >
      {isTopPick && (
        <div className="absolute -top-3 left-5 bg-[#E34A27] text-white text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1 rounded-full flex items-center gap-1">
          <Sparkles size={11} className="shrink-0" /> {translate("ai.topPick")}
        </div>
      )}

      <div className="flex items-start justify-between mb-3 mt-1 gap-3">
        <h2
          data-testid={`${testIdPrefix}-name`}
          className="text-xl font-black tracking-tight text-neutral-900 leading-tight min-w-0"
        >
          {plate.plate_name}
        </h2>
        <div className="text-right shrink-0">
          <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">
            {translate("ai.total")}
          </div>
          <div className="text-lg font-black text-neutral-900">{formatPrice(t.grand)}</div>
          <div className="text-[10px] text-neutral-500 mt-0.5">{translate("ai.plusTaxService")}</div>
        </div>
      </div>

      <ul className="space-y-1 mb-3">
        {plate.items.map((it) => {
          const out = dropped.has(it.id);
          return (
            <li key={it.id} className="text-sm text-neutral-800 flex items-start gap-2">
              <span className="w-1 h-1 rounded-full bg-neutral-400 mt-2 shrink-0" />
              <span className={`flex-1 min-w-0 ${out ? "line-through text-neutral-400" : ""}`}>
                <span className="font-bold">{it.name}</span>
                {it.size && <span className="text-neutral-500"> · {it.size}</span>}
                {it.note && <span className="text-neutral-500 italic"> · {it.note}</span>}
              </span>
              {isDrink(it) && (
                <button
                  data-testid={`${testIdPrefix}-drink-toggle`}
                  onClick={() => toggle(it.id)}
                  aria-label={translate(out ? "ai.drinkKeep" : "ai.drinkDrop")}
                  className="shrink-0 w-6 h-6 rounded-full bg-neutral-100 flex items-center justify-center active:scale-95 transition-transform"
                >
                  {out ? <Plus size={12} className="text-neutral-500" />
                       : <X size={12} className="text-neutral-500" />}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      <div className="grid grid-cols-4 gap-1.5 mb-3">
        <Macro label="P" value={Math.round(t.p)} />
        <Macro label="F" value={Math.round(t.f)} />
        <Macro label="C" value={Math.round(t.c)} />
        <Macro label="Kcal" value={Math.round(t.kcal)} />
      </div>

      {fit && (
        <div
          data-testid={`${testIdPrefix}-fit`}
          className={`inline-flex items-center gap-1.5 mb-3 px-2.5 py-1 rounded-full text-[11px] font-bold ${
            fit.meets_goal
              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
              : "bg-neutral-100 text-neutral-600 border border-neutral-200"
          }`}
        >
          {fit.meets_goal ? <Check size={12} className="shrink-0" /> : <Target size={12} className="shrink-0" />}
          {fit.meets_goal
            ? translate("ai.fitHit", { goal: goalLabel })
            : translate("ai.fitClosest")}
        </div>
      )}

      {plate.why && (
        <div className="border-l-2 border-[#E34A27] pl-3 italic text-[12px] text-neutral-600 leading-relaxed mb-4">
          <span className="not-italic font-bold text-neutral-900">{translate("ai.why")}</span>
          {plate.why}
        </div>
      )}

      {edited && (
        <div className="mb-3 text-[11px] text-neutral-500">{translate("ai.fitEdited")}</div>
      )}

      <button
        data-testid={`${testIdPrefix}-select`}
        disabled={disabled}
        onClick={() => onAccept({ ...plate, items: kept })}
        className={`w-full min-h-12 py-2 px-3 rounded-2xl font-bold text-sm tracking-wide active:scale-[0.98] transition-transform disabled:opacity-50 ${
          isTopPick
            ? "bg-[#E34A27] text-white"
            : "bg-neutral-900 text-white"
        }`}
      >
        {translate("ai.addToOrder")}
      </button>
    </div>
  );
};

const Macro = ({ label, value }) => (
  <div className="bg-neutral-50 rounded-xl py-2 flex flex-col items-center border border-neutral-100">
    <div className="text-[9px] font-mono uppercase tracking-wider text-neutral-500">{label}</div>
    <div className="text-sm font-black text-neutral-900">{value}</div>
  </div>
);

export default AiPlateResult;
